import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2, Plus, Bell, X, Wand2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }
  from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

/**
 * v2.5C Alert Builder — WHEN/AND/OR/THEN composite rules with
 * natural-language preview and per-clause field/op/value.
 */

const FIELDS = [
  { key: "signal_score",            label: "Signal score",       type: "number", suggest: 75 },
  { key: "opportunity_rating",      label: "Opportunity rating", type: "list",
    values: ["A+", "A", "B", "C", "D"] },
  { key: "category",                label: "Market category",    type: "string" },
  { key: "remaining_edge_pct",      label: "Remaining edge",     type: "pct", suggest: 0.5 },
  { key: "n_participating_traders", label: "Traders in signal",  type: "number", suggest: 3 },
  { key: "actionability",           label: "Actionability status", type: "list",
    values: ["potentially_actionable", "limited_remaining_edge", "price_moved", "stale"] },
  { key: "signal_type",             label: "Signal type",        type: "list",
    values: ["MULTI_TRADER_CONVERGENCE", "HIGH_QUALITY_TRADER_ENTRY",
             "UNUSUAL_CONVICTION_INCREASE", "RAPID_CONSENSUS_CHANGE"] },
  { key: "hours_since_detected",    label: "Hours since detected", type: "number", suggest: 24 },
];

const OPS_FOR_TYPE = {
  number: ["gte", "lte", "gt", "lt", "eq", "neq"],
  pct:    ["gte", "lte", "gt", "lt"],
  string: ["eq", "neq"],
  list:   ["eq", "neq", "in", "not_in"],
};

const OP_LABELS = { gte: "≥", lte: "≤", gt: ">", lt: "<", eq: "=", neq: "≠",
                    in: "is one of", not_in: "is not one of" };

function fieldMeta(key) { return FIELDS.find((f) => f.key === key) || FIELDS[0]; }

function makeClause() { return { field: "signal_score", op: "gte", value: 75 }; }

export default function Alerts() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [logic, setLogic] = useState("AND");
  const [clauses, setClauses] = useState([makeClause()]);

  const { data: rules, isLoading } = useQuery({
    queryKey: ["alerts"],
    queryFn: async () => (await api.get("/alerts")).data,
  });

  const conditions = React.useMemo(
    () => ({ logic, clauses: (clauses || []).map(_normClause) }),
    [logic, clauses]);

  const previewQ = useQuery({
    queryKey: ["alert-preview", conditions],
    queryFn: async () =>
      (await api.post("/alerts/preview", conditions)).data,
    enabled: open,
    staleTime: 5_000,
  });

  const createMut = useMutation({
    mutationFn: async () =>
      (await api.post("/alerts", {
        name, enabled: true, conditions,
        delivery_channels: ["in_app"],
      })).data,
    onSuccess: () => {
      setName(""); setLogic("AND"); setClauses([makeClause()]);
      setOpen(false); qc.invalidateQueries({ queryKey: ["alerts"] });
      api.post("/onboarding/progress/enabled_alert").catch(() => {});
    },
  });

  const toggleMut = useMutation({
    mutationFn: async ({ id, enabled }) => api.patch(`/alerts/${id}`, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/alerts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });

  return (
    <div className="space-y-4" data-testid="alerts-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            Personalization
          </div>
          <h1 className="font-heading text-2xl font-bold text-zinc-100">Alert rules</h1>
          <p className="text-xs text-zinc-500 mt-1">
            Compose WHEN / AND · OR / THEN rules. The rule builder writes plain English as you go.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-new-alert" className="h-8 text-xs bg-zinc-100 text-zinc-950 hover:bg-white">
              <Plus size={12} className="mr-1" /> New alert
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading">Build an alert rule</DialogTitle>
              <DialogDescription className="text-zinc-500 text-xs">
                Add one or more conditions. Combine them with AND or OR. The natural-language preview updates live.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="text-xs text-zinc-400">Rule name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="e.g. High-score political signals"
                       data-testid="input-alert-name"
                       className="mt-1 bg-zinc-900 border-zinc-800" />
              </div>

              {/* WHEN header + logic switch */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">WHEN</span>
                <span className="text-xs text-zinc-400">at least</span>
                <Select value={logic} onValueChange={setLogic}>
                  <SelectTrigger className="h-7 w-24 bg-zinc-900 border-zinc-800 text-xs"
                                 data-testid="select-alert-logic">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AND">ALL</SelectItem>
                    <SelectItem value="OR">ANY</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-zinc-400">of these are true</span>
              </div>

              {/* Clauses */}
              <div className="space-y-2" data-testid="alert-clauses">
                {clauses.map((c, i) => (
                  <ClauseRow
                    key={i}
                    clause={c}
                    onChange={(next) => {
                      const copy = [...clauses];
                      copy[i] = next;
                      setClauses(copy);
                    }}
                    onRemove={clauses.length === 1 ? null : () => {
                      setClauses(clauses.filter((_, k) => k !== i));
                    }}
                    index={i}
                  />
                ))}
                <button
                  onClick={() => setClauses([...clauses, makeClause()])}
                  data-testid="btn-add-clause"
                  className="text-xs text-zinc-400 hover:text-zinc-100 inline-flex items-center gap-1 mt-1"
                >
                  <Plus size={12} /> Add condition
                </button>
              </div>

              {/* THEN */}
              <div className="flex items-center gap-2 text-xs text-zinc-400">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">THEN</span>
                <span>send an in-app notification.</span>
              </div>

              {/* Preview */}
              <div className="border border-zinc-800 rounded-md p-3 bg-zinc-900/40" data-testid="alert-preview">
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-blue-300/70 mb-1">
                  <Wand2 size={11} /> Rule preview
                </div>
                <div className="text-sm text-zinc-100">
                  {previewQ.isPending ? (
                    <span className="text-zinc-500 inline-flex items-center gap-1">
                      <Loader2 size={11} className="animate-spin" /> writing…
                    </span>
                  ) : (previewQ.data?.preview || "—")}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                data-testid="btn-create-alert"
                onClick={() => createMut.mutate()}
                disabled={!name || createMut.isPending || clauses.length === 0}
                className="bg-zinc-100 text-zinc-950 hover:bg-white h-8 text-xs"
              >
                {createMut.isPending && <Loader2 size={11} className="mr-1 animate-spin" />}
                Create rule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading && <div className="text-xs text-zinc-500">Loading…</div>}
      <div className="border border-zinc-800 rounded-md divide-y divide-zinc-800/70">
        {(rules || []).length === 0 && !isLoading && (
          <div className="p-6 text-center text-xs text-zinc-500">
            <Bell size={20} className="mx-auto text-zinc-600 mb-2" />
            No alert rules yet. Create one to receive in-app notifications.
          </div>
        )}
        {(rules || []).map((r) => (
          <RuleRow
            key={r.id}
            rule={r}
            onToggle={(v) => toggleMut.mutate({ id: r.id, enabled: v })}
            onDelete={() => delMut.mutate(r.id)}
          />
        ))}
      </div>
    </div>
  );
}

function _normClause(c) {
  const meta = fieldMeta(c.field);
  const value = c.value;
  if (meta.type === "number" || meta.type === "pct") {
    if (Array.isArray(value)) return { ...c, value };
    const num = Number(value);
    return { ...c, value: isNaN(num) ? 0 : num };
  }
  return c;
}

function ClauseRow({ clause, onChange, onRemove, index }) {
  const meta = fieldMeta(clause.field);
  const ops = OPS_FOR_TYPE[meta.type] || OPS_FOR_TYPE.string;
  const setField = (key) => {
    const m = fieldMeta(key);
    const first = OPS_FOR_TYPE[m.type][0];
    const val = m.suggest != null
      ? m.suggest
      : (m.type === "list" ? (first === "in" || first === "not_in" ? [] : m.values?.[0]) : "");
    onChange({ field: key, op: first, value: val });
  };
  const setOp = (op) => {
    // Reset value shape when switching between single/list ops
    if ((op === "in" || op === "not_in") && !Array.isArray(clause.value)) {
      onChange({ ...clause, op, value: [] });
    } else if ((clause.op === "in" || clause.op === "not_in") &&
               (op !== "in" && op !== "not_in")) {
      onChange({ ...clause, op, value: meta.values?.[0] || "" });
    } else {
      onChange({ ...clause, op });
    }
  };
  const setValue = (v) => onChange({ ...clause, value: v });

  return (
    <div className="grid grid-cols-12 gap-2 items-center"
         data-testid={`alert-clause-${index}`}>
      <Select value={clause.field} onValueChange={setField}>
        <SelectTrigger className="col-span-4 h-8 bg-zinc-900 border-zinc-800 text-xs"
                       data-testid={`clause-field-${index}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FIELDS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={clause.op} onValueChange={setOp}>
        <SelectTrigger className="col-span-3 h-8 bg-zinc-900 border-zinc-800 text-xs"
                       data-testid={`clause-op-${index}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ops.map((op) => <SelectItem key={op} value={op}>{OP_LABELS[op]}</SelectItem>)}
        </SelectContent>
      </Select>

      <ClauseValueInput
        meta={meta} op={clause.op}
        value={clause.value} onChange={setValue}
        index={index}
      />

      {onRemove && (
        <button onClick={onRemove}
                data-testid={`clause-remove-${index}`}
                className="col-span-1 text-zinc-500 hover:text-rose-400">
          <X size={14} />
        </button>
      )}
    </div>
  );
}

function ClauseValueInput({ meta, op, value, onChange, index }) {
  const isMulti = op === "in" || op === "not_in";

  if (meta.type === "list") {
    if (isMulti) {
      const set = new Set(Array.isArray(value) ? value : []);
      return (
        <div className="col-span-4 flex flex-wrap gap-1">
          {meta.values.map((v) => {
            const active = set.has(v);
            return (
              <button
                key={v}
                onClick={() => {
                  const next = new Set(set);
                  if (active) next.delete(v); else next.add(v);
                  onChange(Array.from(next));
                }}
                data-testid={`clause-value-${index}-${v}`}
                className={`text-[10px] px-1.5 py-0.5 rounded border ${
                  active ? "bg-zinc-100 text-zinc-950 border-zinc-100"
                         : "bg-zinc-900 text-zinc-400 border-zinc-800"
                }`}
              >
                {v}
              </button>
            );
          })}
        </div>
      );
    }
    return (
      <div className="col-span-4">
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="h-8 bg-zinc-900 border-zinc-800 text-xs"
                          data-testid={`clause-value-${index}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {meta.values.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <Input
      className="col-span-4 h-8 bg-zinc-900 border-zinc-800 text-xs"
      type={meta.type === "number" || meta.type === "pct" ? "number" : "text"}
      step={meta.type === "pct" ? "0.05" : undefined}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`clause-value-${index}`}
      placeholder={meta.type === "pct" ? "0.0 – 1.0" : ""}
    />
  );
}

function RuleRow({ rule, onToggle, onDelete }) {
  const [preview, setPreview] = useState(null);
  React.useEffect(() => {
    let mounted = true;
    api.post("/alerts/preview", rule.conditions || {})
       .then((r) => { if (mounted) setPreview(r.data.preview); })
       .catch(() => {});
    return () => { mounted = false; };
  }, [rule.conditions]);

  return (
    <div className="flex items-center justify-between px-4 py-3"
         data-testid={`alert-rule-${rule.id}`}>
      <div className="min-w-0">
        <div className="font-heading font-semibold text-zinc-100">{rule.name}</div>
        <div className="text-[11px] text-zinc-500 mt-0.5">
          {preview || "…"}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Switch
          checked={!!rule.enabled}
          onCheckedChange={onToggle}
          data-testid={`switch-alert-${rule.id}`}
        />
        <button
          onClick={onDelete}
          data-testid={`btn-del-alert-${rule.id}`}
          className="text-zinc-500 hover:text-rose-400"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}
